import * as gtag from "../../../lib/gtag"
import { formatRegistrationNumber } from "@today/lib"
import { Container } from "../Container"
import { Button } from "baseui/button"
import { Input, InputOverrides } from "baseui/input"
import { FormControl } from "baseui/form-control"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useFetcher } from "@today/api"
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaTimesCircle,
} from "react-icons/fa"
import axios, { AxiosError } from "axios"
import { SMERegistrationTerm } from "../SMERegistrationTerm"
import { LoadingOverlay } from "../../common/LoadingOverlay"
import { useUserInfo } from "@today/auth/keycloak"
import { Label } from "../Label"
import { isPhoneNumberCorrect, checkPassword } from "../validations"
import AuthenticationGuard from "./AuthenticationGuard"
import RegisteredSuccessfully from "./RegisteredSuccessfully"

import { Step, gtagEvent, Category, FocusItem, CompanyNameStatus } from "./type"
import StepHeading from "./StepHeading"
import StepNotice from "./StepNotice"
import ConfirmCompanyInfo from "./ConfirmCompanyInfo"
import useSMERegistrationFocusRef from "./useSMERegistrationFocusRef"
// todo
// import useSMERegistrationEffect from "./useSMERegistrationEffect"
import { Radio, RadioGroup } from "baseui/radio"
import { SearchCompanyErrorCode } from "../../../pages/api/register/search-company"

function getStepName(step: Step) {
  switch (step) {
    case Step.InputRegistrationNumber:
      return "input_registration_number"
    case Step.LoadingCompanyInfo:
      return "loading_company_info"
    case Step.InputCustomCompanyName:
      return "input_custom_company_name"
    case Step.ConfirmCompanyInfo:
      return "confirm_company_info"
    case Step.InputPhoneNumber:
      return "input_phone_number"
    case Step.SendingPhoneNumberVerificationKey:
      return "sending_phone_number_verification_key"
    case Step.InputPhoneNumberVerificationKey:
      return "input_phone_number_verification_key"
    case Step.CheckingPhoneNumberVerificationKey:
      return "checking_phone_number_verification_key"
    case Step.InputPassword:
      return "input_password"
    case Step.RepeatPassword:
      return "repeat_password"
    case Step.BeforeReadingTerm:
      return "before_reading_term"
    case Step.AgreeTerm:
      return "agree_term"
    case Step.AllDone:
      return "all_done"
  }
}

function getCompanyErrorMessage(errorCode: SearchCompanyErrorCode) {
  switch (errorCode) {
    case "ALREADY_REGISTERED":
      return "이미 가입된 회사입니다. 비밀번호를 잊으셨나요? 상단 메뉴의 비밀번호 변경 버튼을 클릭해주세요."
    case "INVALID_REGISTRATION_NUMBER":
      return "존재하지 않는 사업자등록번호입니다."
    case "CLOSED":
      return "폐업한 사업자는 가입이 불가능합니다."
    case "SUSPENDED":
      return "휴업 중인 사업자는 가입이 불가능합니다."
    case "FAIL_TO_GET_COMPANY_NAME":
    case "INTERNAL_ERROR":
      return "에러가 발생하였습니다. 잠시 후 다시 시도해주세요."
  }
}

function sendEvent(
  event: gtagEvent,
  label: string,
  registrationNumber: string,
  category: Category = "incheon_registration"
) {
  gtag.event(
    {
      action: event,
      label,
      category,
      value: 1,
    },
    {
      ...(registrationNumber
        ? { registration_number: formatRegistrationNumber(registrationNumber) }
        : {}),
    }
  )
}

function SMERegistrationPage() {
  const fetcher = useFetcher()
  const { name: clientName, isAuthenticated } = useUserInfo()

  // state
  const [currentStep, setCurrentStepBase] = useState(
    Step.InputRegistrationNumber
  )
  const [currentVerificationTransaction, setCurrentVerificationTransaction] =
    useState<
      | Step.InputPhoneNumber
      | Step.InputPhoneNumberVerificationKey
      | Step.CheckingPhoneNumberVerificationKey
      | Step.AllDone
    >(Step.InputPhoneNumber)
  const [registrationNumber, setRegistrationNumber] = useState("")
  const [companyName, setCompanyName] = useState<string>()
  const [companyNameStatus, setCompanyNameStatus] =
    useState<CompanyNameStatus>()
  const [
    invalidRegistrationNumberErrorMessage,
    setInvalidRegistrationNumberErrorMessage,
  ] = useState<string>()
  const isValidRegistrationNumber = !invalidRegistrationNumberErrorMessage
  const [registrationNumberTrigger, setRegistrationNumberTrigger] = useState(0)
  const [phoneNumber, setPhoneNumber] = useState("")
  const [transactionId, setTransactionId] = useState()
  const [verificationCode, setVerificationCode] = useState("")
  const [hasPhoneNumberVerified, setHasPhoneNumberVerified] = useState(false)
  const [verificationCodeErrorMessage, setVerificationCodeErrorMessage] =
    useState<string>()
  const [password, setPassword] = useState("")
  const [hasPasswordChecked, setHasPasswordChecked] = useState(false)
  const [repeatedPassword, setRepeatedPassword] = useState("")
  const [hasRepeatedPasswordChecked, setHasRepeatedPasswordChecked] =
    useState(false)
  const [showPasswordsMismatched, setShowPasswordsMismatched] = useState(false)
  const [hasAgreedTerm, setHasAgreedTerm] = useState(false)
  const [hasReachedEndOfTerm, setHasReachedEndOfTerm] = useState(false)
  const [registerProcess, setRegisterProcess] = useState<
    "isRegistering" | "registeredSuccessfully"
  >()
  const [isFirstInputCustomCompanyName, setFirstInputCustomCompanyName] =
    useState(true)
  const [recommender, setRecommender] = useState("없음")

  // custom-hook
  const {
    currentFocus,
    setCurrentFocus,
    inputCompanyNameRef,
    inputPhoneRef,
    inputRegistrationNumberRef,
    inputVerificationCodeRef,
    inputPasswordRef,
    inputRepeatPasswordRef,
    buttonReadTermRef,
    buttonRegisterRef,
  } = useSMERegistrationFocusRef({ currentStep })

  // ref
  const currentVerificationTransactionRef = useRef(
    currentVerificationTransaction
  )
  const scrollTermRef = useRef<HTMLDivElement>(null)

  // memorization
  const setCurrentStep = useCallback((step: Step) => {
    switch (step) {
      case Step.InputPhoneNumber:
        setCurrentStepBase(currentVerificationTransactionRef.current)
        return
      case Step.InputPhoneNumberVerificationKey:
      case Step.CheckingPhoneNumberVerificationKey:
      case Step.InputPassword:
        const transactionStep =
          step === Step.InputPassword ? Step.AllDone : step
        currentVerificationTransactionRef.current = transactionStep
        setCurrentVerificationTransaction(transactionStep)
        setCurrentStepBase(step)
        return
      default:
        setCurrentStepBase(step)
        return
    }
  }, [])
  const formattedRegistrationNumber = useMemo(
    () =>
      [
        registrationNumber.slice(0, 3),
        registrationNumber.slice(3, 5),
        registrationNumber.slice(5),
      ]
        .filter((token) => !!token)
        .join("-"),
    [registrationNumber]
  )
  const formattedPhoneNumber = useMemo(
    () =>
      [
        phoneNumber.slice(0, 3),
        phoneNumber.slice(3, phoneNumber.length === 10 ? 6 : 7),
        phoneNumber.slice(phoneNumber.length === 10 ? 6 : 7),
      ]
        .filter((token) => !!token)
        .join("-"),
    [phoneNumber]
  )
  const verifyPhoneNumber = useCallback(
    (phoneNumber: string) => {
      if (!isPhoneNumberCorrect(phoneNumber)) {
        return
      }
      setCurrentStep(Step.SendingPhoneNumberVerificationKey)
      ;(async () => {
        await fetcher("/api/register/send-phone-verification-code", {
          phoneNumber,
        })
        setCurrentStep(Step.InputPhoneNumberVerificationKey)
      })()
    },
    [fetcher, setCurrentStep]
  )
  const checkVerificationCode = useCallback(
    (verificationCode: string) => {
      setCurrentStep(Step.CheckingPhoneNumberVerificationKey)
      setVerificationCodeErrorMessage(undefined)
      ;(async () => {
        try {
          const data = await fetcher(
            "/api/register/check-phone-verification-code",
            {
              phoneNumber,
              verificationCode,
            }
          )
          if (data.transactionId) {
            setTransactionId(data.transactionId)
          }
          setCurrentStep(Step.InputPassword)
          setVerificationCode("")
          setHasPhoneNumberVerified(false)
        } catch (error) {
          const defaultErrorMessage =
            "에러가 발생하였습니다. 잠시 후 다시 시도해주세요."
          const errorMessage = axios.isAxiosError(error)
            ? (error as AxiosError).response?.data?.errorMessage ??
              defaultErrorMessage
            : defaultErrorMessage
          setVerificationCodeErrorMessage(errorMessage)
          setCurrentStep(Step.InputPhoneNumberVerificationKey)
          setHasPhoneNumberVerified(true)
        }
      })()
    },
    [phoneNumber]
  )
  const { isGoodPassword, passwordErrorMessage } = useMemo(
    () => checkPassword(password),
    [password]
  )
  const canRegister = useMemo(
    () =>
      registrationNumber.length === 10 &&
      isValidRegistrationNumber &&
      isPhoneNumberCorrect(phoneNumber) &&
      Boolean(transactionId) &&
      isGoodPassword &&
      Boolean(password) &&
      password === repeatedPassword &&
      hasAgreedTerm,
    [
      registrationNumber.length,
      isValidRegistrationNumber,
      phoneNumber,
      transactionId,
      isGoodPassword,
      password,
      repeatedPassword,
      hasAgreedTerm,
    ]
  )
  const register = useCallback(async () => {
    if (!canRegister) {
      return
    }
    setRegisterProcess("isRegistering")
    try {
      sendEvent("register_account", "start_registration", registrationNumber)
      await fetcher("/api/register", {
        transactionId,
        registrationNumber,
        phoneNumber,
        password,
        companyName,
      })
      sendEvent("register_account", "registration_success", registrationNumber)
      setRegisterProcess("registeredSuccessfully")
    } catch {
      alert("생성 중 에러가 발생했습니다. 잠시 후 다시 시도해주세요.")
      sendEvent("register_account", "registration_failed", registrationNumber)
      setRegisterProcess(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationNumber, phoneNumber, transactionId, password, canRegister])
  const inputOverrides = useCallback(
    (focus: FocusItem): InputOverrides => ({
      Root: {
        style:
          currentFocus === focus
            ? {
                borderColor: "#000",
              }
            : {},
      },
      Input: {
        style: ({ disabled }) =>
          disabled
            ? {
                color: "#888",
                "-webkitTextFillColor": "#888",
                textFillColor: "#888",
              }
            : {},
        ...(focus === "password"
          ? {
              props: {
                minlength: "10",
                maxlength: "32",
                passwordrules:
                  "required: digit; required: upper,lower; allowed: [!#$%&'()*+,./:;<=>?@\\^_`{|}~];",
              },
            }
          : {}),
      },
    }),
    [currentFocus]
  )

  // effect
  const registrationNumberRef = useRef("")
  const companyNameCacheRef = useRef(new Map<string, string>())
  const prevStepRef = useRef(currentStep)
  const prevCompanyName = useRef(companyName)

  useEffect(() => {
    sendEvent("change_step", getStepName(currentStep), registrationNumber)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  useEffect(() => {
    setInvalidRegistrationNumberErrorMessage(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationNumber])

  useEffect(() => {
    if (registrationNumber.length !== 10) return
    setInvalidRegistrationNumberErrorMessage(undefined)

    registrationNumberRef.current = registrationNumber
    const cachedCompanyName =
      companyNameCacheRef.current.get(registrationNumber)
    if (cachedCompanyName) {
      setCompanyName(cachedCompanyName)
      setCurrentStep(Step.ConfirmCompanyInfo)
      return
    }
    setCurrentStep(Step.LoadingCompanyInfo)
    ;(async () => {
      try {
        const data = await fetcher<{
          registrationNumber: string
          companyName: string
        }>(
          `/api/register/search-company?registrationNumber=${registrationNumber}`
        )
        companyNameCacheRef.current.set(
          data.registrationNumber,
          data.companyName
        )
        if (data.registrationNumber === registrationNumberRef.current) {
          setCompanyName(data.companyName)
          setCompanyNameStatus("AUTOMATIC")
          setCurrentStep(Step.ConfirmCompanyInfo)
        }
      } catch (error) {
        if (registrationNumber !== registrationNumberRef.current) {
          // 과거 데이터에서 난 에러는 무시
          return
        }
        const errorCode = (
          axios.isAxiosError(error)
            ? (error as AxiosError).response?.data?.errorCode ??
              "INTERNAL_ERROR"
            : "INTERNAL_ERROR"
        ) as SearchCompanyErrorCode
        if (errorCode === "NOT_REGISTERED_IN_NTS") {
          sendEvent(
            "get_company_info",
            "not_registered_in_nts",
            registrationNumber
          )
          setInvalidRegistrationNumberErrorMessage(
            "국세청에 등록되지 않은 사업자 등록번호입니다. 문제가 계속될 경우 고객센터로 문의주시길 바랍니다."
          )
          setCurrentStep(Step.InputRegistrationNumber)
        } else if (errorCode !== "FAIL_TO_GET_COMPANY_NAME") {
          setInvalidRegistrationNumberErrorMessage(
            getCompanyErrorMessage(errorCode)
          )
          setCurrentStep(Step.InputRegistrationNumber)
        } else {
          sendEvent(
            "get_company_info",
            "fail_to_get_company_name",
            registrationNumber
          )
          setCompanyNameStatus("CUSTOM")
          setCurrentStep(Step.InputCustomCompanyName)
        }
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationNumber, registrationNumberTrigger])

  useEffect(() => {
    setVerificationCodeErrorMessage(undefined)
    if (verificationCode.length === 6) {
      checkVerificationCode(verificationCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkVerificationCode, verificationCode])

  useEffect(() => {
    setShowPasswordsMismatched(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatedPassword, password])

  useEffect(() => {
    if (isGoodPassword && repeatedPassword === password) {
      setCurrentStep(Step.BeforeReadingTerm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatedPassword, password, isGoodPassword])

  useEffect(() => {
    if (currentStep < Step.AgreeTerm) return
    const handleScroll = () => {
      if (scrollTermRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollTermRef.current
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          setHasReachedEndOfTerm(true)
        }
      }
    }
    const scrollElement = scrollTermRef.current
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll)
    }
    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", handleScroll)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  useEffect(() => {
    if (currentStep !== Step.AgreeTerm) {
      return
    }
    if (hasReachedEndOfTerm) {
      setCurrentFocus("register")
      buttonRegisterRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReachedEndOfTerm, currentStep])

  useEffect(() => {
    if (canRegister) {
      setCurrentStep(Step.AllDone)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRegister])
  useEffect(() => {
    if (
      prevStepRef.current === Step.InputCustomCompanyName &&
      currentStep > Step.InputCustomCompanyName &&
      companyName?.length
    ) {
      setFirstInputCustomCompanyName(false)
    }
    prevStepRef.current = currentStep
    prevCompanyName.current = companyName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, currentStep])

  return (
    <Container>
      {isAuthenticated ? (
        <AuthenticationGuard clientName={clientName} />
      ) : registerProcess === "isRegistering" ? (
        <div
          className="fixed left-0 top-0 h-screen w-screen"
          style={{ height: "100dvh" }}
        >
          <LoadingOverlay />
        </div>
      ) : registerProcess === "registeredSuccessfully" ? (
        <RegisteredSuccessfully companyName={companyName} />
      ) : (
        <>
          <StepHeading
            currentStep={currentStep}
            companyNameStatus={companyNameStatus}
            isFirstInputCustomCompanyName={isFirstInputCustomCompanyName}
            companyName={companyName}
            canRegister={canRegister}
          />
          <StepNotice />
          <div className="relative flex w-full flex-1 flex-col overflow-y-hidden">
            <div className="w-full overflow-y-scroll">
              <div className="flex h-full flex-col-reverse justify-end md:flex-col md:justify-start">
                <div>
                  <FormControl
                    label={
                      <Label
                        bold={currentFocus === "registrationNumber"}
                        tip="(10자리 숫자)"
                      >
                        사업자등록번호
                      </Label>
                    }
                    caption={
                      currentStep === Step.InputRegistrationNumber &&
                      invalidRegistrationNumberErrorMessage && (
                        <p className="text-red-500">
                          * {invalidRegistrationNumberErrorMessage}
                        </p>
                      )
                    }
                  >
                    <Input
                      inputRef={inputRegistrationNumberRef}
                      autoComplete="username"
                      inputMode="numeric"
                      disabled={
                        currentStep > Step.ConfirmCompanyInfo &&
                        !!companyNameStatus
                      }
                      value={formattedRegistrationNumber}
                      onChange={(event) => {
                        setCompanyName("")
                        setCurrentStep(Step.InputRegistrationNumber)
                        setRegistrationNumber(
                          event.target.value.replace(/\D/g, "").slice(0, 10)
                        )
                      }}
                      onFocus={() => setCurrentFocus("registrationNumber")}
                      overrides={inputOverrides("registrationNumber")}
                      endEnhancer={
                        currentStep === Step.LoadingCompanyInfo ? (
                          <FaSpinner className="animate-spin" />
                        ) : currentStep === Step.InputCustomCompanyName ? (
                          <FaExclamationTriangle className="text-yellow-500" />
                        ) : !!companyNameStatus ? (
                          <FaCheckCircle className="text-green-500" />
                        ) : currentStep === Step.InputRegistrationNumber &&
                          invalidRegistrationNumberErrorMessage ? (
                          <FaTimesCircle className="text-red-500" />
                        ) : null
                      }
                    />
                  </FormControl>
                </div>
                <div
                  className={
                    currentStep === Step.InputCustomCompanyName ||
                    companyNameStatus === "CUSTOM" ||
                    companyNameStatus === "CUSTOM_CONFIRMED"
                      ? "animate-fadeIn block md:animate-none"
                      : "hidden"
                  }
                >
                  <FormControl
                    label={
                      <Label
                        bold={currentFocus === "companyName"}
                        tip="(사업자등록증에 기재된 사업자명)"
                      >
                        사업자명
                      </Label>
                    }
                  >
                    <Input
                      inputRef={inputCompanyNameRef}
                      autoComplete="organization"
                      value={companyName}
                      disabled={
                        currentStep > Step.InputCustomCompanyName &&
                        companyNameStatus !== "CUSTOM"
                      }
                      onChange={(event) => {
                        setCurrentStep(Step.InputCustomCompanyName)
                        setCompanyName(event.target.value)
                      }}
                      onFocus={() => setCurrentFocus("companyName")}
                      overrides={inputOverrides("companyName")}
                    />
                  </FormControl>
                </div>
                <div
                  className={
                    currentStep >= Step.InputPhoneNumber
                      ? "animate-fadeIn block md:animate-none"
                      : "hidden md:block"
                  }
                >
                  <FormControl
                    label={
                      <Label bold={currentFocus === "phone"}>
                        담당자 휴대전화
                      </Label>
                    }
                  >
                    <Input
                      inputRef={inputPhoneRef}
                      autoComplete="mobile tel-national"
                      type="tel"
                      inputMode="tel"
                      value={formattedPhoneNumber}
                      disabled={currentStep >= Step.InputPassword}
                      onChange={(event) => {
                        setCurrentStep(Step.InputPhoneNumber)
                        const phoneNumber = event.target.value
                          .replace(/\D/g, "")
                          .slice(0, 11)
                        setPhoneNumber(phoneNumber)
                      }}
                      onFocus={() => setCurrentFocus("phone")}
                      overrides={inputOverrides("phone")}
                      endEnhancer={
                        transactionId ? (
                          <FaCheckCircle className="text-green-500" />
                        ) : currentStep === Step.InputPhoneNumber &&
                          !isPhoneNumberCorrect(phoneNumber) ? (
                          phoneNumber.length >= 10 && (
                            <FaExclamationTriangle className="text-yellow-500" />
                          )
                        ) : isPhoneNumberCorrect(phoneNumber) ? (
                          <Button
                            className="h-8 w-14"
                            size="mini"
                            isLoading={
                              currentStep ===
                              Step.SendingPhoneNumberVerificationKey
                            }
                            onClick={() => {
                              verifyPhoneNumber(phoneNumber)
                            }}
                          >
                            {currentStep ===
                            Step.InputPhoneNumberVerificationKey
                              ? "재전송"
                              : "인증"}
                          </Button>
                        ) : null
                      }
                    />
                  </FormControl>
                </div>
                <div
                  className={
                    currentVerificationTransaction ===
                      Step.InputPhoneNumberVerificationKey ||
                    currentVerificationTransaction ===
                      Step.CheckingPhoneNumberVerificationKey
                      ? "animate-fadeIn block md:animate-none"
                      : "hidden"
                  }
                >
                  <FormControl
                    label={
                      <Label
                        bold={currentFocus === "verificationCode"}
                        tip={
                          <span className="text-red-500">
                            (카카오톡 또는 SMS를 확인해주세요)
                          </span>
                        }
                      >
                        6자리 인증코드 입력
                      </Label>
                    }
                    caption={
                      verificationCodeErrorMessage && (
                        <p className="text-red-500">
                          * {verificationCodeErrorMessage}
                        </p>
                      )
                    }
                  >
                    <Input
                      inputRef={inputVerificationCodeRef}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      value={verificationCode}
                      onChange={(event) => {
                        const verificationCode = event.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6)
                        setVerificationCode(verificationCode)
                      }}
                      onFocus={() => setCurrentFocus("verificationCode")}
                      overrides={inputOverrides("verificationCode")}
                      endEnhancer={
                        currentStep ===
                        Step.CheckingPhoneNumberVerificationKey ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          hasPhoneNumberVerified &&
                          verificationCode.length === 6 && (
                            <FaTimesCircle className="text-red-500" />
                          )
                        )
                      }
                    />
                  </FormControl>
                </div>
                <div
                  className={
                    currentStep >= Step.InputPassword
                      ? "animate-fadeIn block md:animate-none"
                      : "hidden md:block"
                  }
                >
                  <FormControl
                    label={
                      <Label bold={currentFocus === "password"}>비밀번호</Label>
                    }
                    caption={
                      hasPasswordChecked &&
                      password.length > 0 &&
                      !isGoodPassword && (
                        <p className="text-red-500">* {passwordErrorMessage}</p>
                      )
                    }
                  >
                    <Input
                      inputRef={inputPasswordRef}
                      autoComplete="new-password"
                      value={password}
                      type="password"
                      onChange={(event) => {
                        setPassword(event.target.value)
                      }}
                      onFocus={() => setCurrentFocus("password")}
                      onBlur={() => {
                        if (password.length > 0) {
                          setHasPasswordChecked(true)
                        }
                      }}
                      overrides={inputOverrides("password")}
                      endEnhancer={
                        isGoodPassword ? (
                          <FaCheckCircle className="text-green-500" />
                        ) : (
                          password.length > 0 && (
                            <FaExclamationTriangle className="text-yellow-500" />
                          )
                        )
                      }
                    />
                  </FormControl>
                </div>
                <div
                  className={
                    currentStep >= Step.RepeatPassword
                      ? "animate-fadeIn block md:animate-none"
                      : "hidden md:block"
                  }
                >
                  <FormControl
                    label={
                      <Label
                        bold={currentFocus === "repeatingPassword"}
                        tip={
                          <>
                            <span className="md:hidden">
                              (아래 입력하신 비밀번호를 한 번 더 입력해주세요)
                            </span>
                            <span className="hidden md:inline">
                              (위에 입력하신 비밀번호를 한 번 더 입력해주세요)
                            </span>
                          </>
                        }
                      >
                        비밀번호 확인
                      </Label>
                    }
                    caption={
                      showPasswordsMismatched && (
                        <p className="text-red-500">
                          * 비밀번호가 일치하지 않습니다.
                        </p>
                      )
                    }
                  >
                    <Input
                      inputRef={inputRepeatPasswordRef}
                      autoComplete="off"
                      value={repeatedPassword}
                      type="password"
                      onChange={(event) => {
                        setRepeatedPassword(event.target.value)
                      }}
                      onFocus={() => setCurrentFocus("repeatingPassword")}
                      onBlur={() => {
                        if (repeatedPassword.length === 0) {
                          return
                        }
                        setHasRepeatedPasswordChecked(true)
                        if (password !== repeatedPassword) {
                          setShowPasswordsMismatched(true)
                        }
                      }}
                      overrides={inputOverrides("password")}
                      endEnhancer={
                        password.length === 0 ? null : repeatedPassword ===
                          password ? (
                          <FaCheckCircle className="text-green-500" />
                        ) : (
                          hasRepeatedPasswordChecked && (
                            <FaTimesCircle className="text-red-500" />
                          )
                        )
                      }
                    />
                  </FormControl>
                </div>

                <div>
                  <FormControl label={<Label bold={false}>추천인</Label>}>
                    <RadioGroup
                      value={recommender}
                      onChange={(e) => setRecommender(e.currentTarget.value)}
                      name="recommender"
                      overrides={{
                        RadioGroupRoot: {
                          style: {
                            flexDirection: "row",
                            gap: "1rem",
                          },
                        },
                      }}
                    >
                      {[
                        "검암역",
                        "계양역",
                        "부평구청역",
                        "인천시청역",
                        "원인재역",
                        "딜리",
                        "없음",
                      ].map((value) => (
                        <Radio key={value} value={value}>
                          {value}
                        </Radio>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </div>
                <div
                  className={
                    currentStep >= Step.BeforeReadingTerm
                      ? "animate-fadeIn block md:animate-none"
                      : "hidden md:block"
                  }
                >
                  <FormControl
                    label={
                      <Label bold={currentFocus === "readTerm"}>
                        약관 동의
                      </Label>
                    }
                  >
                    <Button
                      ref={buttonReadTermRef}
                      className="w-full focus:outline-none focus:ring-0 focus:ring-offset-0"
                      size="compact"
                      kind="secondary"
                      onFocus={() => setCurrentFocus("readTerm")}
                      onClick={() => setCurrentStep(Step.AgreeTerm)}
                      endEnhancer={
                        hasAgreedTerm ? (
                          <FaCheckCircle className="text-green-500" />
                        ) : null
                      }
                    >
                      이용약관 읽기
                      {hasAgreedTerm && (
                        <span className="ml-1 font-bold">[동의완료]</span>
                      )}
                    </Button>
                  </FormControl>
                </div>
              </div>
              {currentStep === Step.AgreeTerm && (
                <div className="animate-slideLeft absolute left-0 top-0 h-full w-full border border-gray-900 bg-white p-3">
                  <SMERegistrationTerm ref={scrollTermRef} />
                </div>
              )}
            </div>
          </div>
          <div className="flex w-full">
            <Button
              ref={buttonRegisterRef}
              className="flex-grow shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0 md:hidden"
              disabled={
                (currentStep === Step.InputRegistrationNumber &&
                  registrationNumber.length < 10) ||
                (currentStep === Step.InputRegistrationNumber &&
                  !!invalidRegistrationNumberErrorMessage) ||
                (currentStep === Step.InputCustomCompanyName && !companyName) ||
                currentStep === Step.LoadingCompanyInfo ||
                (currentStep === Step.InputPhoneNumber &&
                  !isPhoneNumberCorrect(phoneNumber)) ||
                currentStep === Step.SendingPhoneNumberVerificationKey ||
                (currentStep === Step.InputPhoneNumberVerificationKey &&
                  verificationCode.length < 6) ||
                (currentStep >= Step.InputPassword &&
                  currentStep !== Step.AgreeTerm &&
                  password.length === 0) ||
                (currentStep === Step.RepeatPassword &&
                  password.length !== repeatedPassword.length) ||
                (currentStep === Step.RepeatPassword &&
                  showPasswordsMismatched) ||
                (currentStep === Step.AgreeTerm && !hasReachedEndOfTerm) ||
                (currentStep === Step.AllDone && !canRegister)
              }
              onClick={() => {
                switch (currentStep) {
                  case Step.InputRegistrationNumber:
                    setRegistrationNumberTrigger((n) => n + 1)
                    break
                  case Step.InputCustomCompanyName:
                    if (companyName) {
                      setCurrentStep(Step.ConfirmCompanyInfo)
                    }
                    break
                  case Step.InputPhoneNumber:
                    if (isPhoneNumberCorrect(phoneNumber)) {
                      verifyPhoneNumber(phoneNumber)
                    }
                    break
                  case Step.InputPhoneNumberVerificationKey:
                    if (verificationCode.length === 6) {
                      checkVerificationCode(verificationCode)
                    }
                    break
                  case Step.InputPassword:
                    setHasPasswordChecked(true)
                    if (isGoodPassword) {
                      setCurrentStep(Step.RepeatPassword)
                    }
                    break
                  case Step.RepeatPassword:
                    setHasRepeatedPasswordChecked(true)
                    if (isGoodPassword && password === repeatedPassword) {
                      setCurrentStep(Step.BeforeReadingTerm)
                    } else {
                      setShowPasswordsMismatched(true)
                    }
                    break
                  case Step.BeforeReadingTerm:
                    setCurrentStep(Step.AgreeTerm)
                    break
                  case Step.AgreeTerm:
                    if (hasReachedEndOfTerm) {
                      sendEvent(
                        "click_button",
                        "term_agreement",
                        registrationNumber
                      )
                      setHasAgreedTerm(true)
                      setCurrentStep(Step.AllDone)
                    }
                    break
                  case Step.AllDone:
                    sendEvent(
                      "click_button",
                      "submit_registration",
                      registrationNumber
                    )
                    if (canRegister) {
                      register()
                    }
                }
              }}
            >
              {currentStep === Step.AgreeTerm
                ? hasReachedEndOfTerm
                  ? "동의합니다"
                  : "약관을 끝까지 읽어주세요"
                : currentStep === Step.AllDone
                ? "회원가입 하기"
                : "다음"}
            </Button>
            <Button
              className="hidden flex-grow md:block"
              disabled={
                (currentStep !== Step.AgreeTerm && !canRegister) ||
                (currentStep === Step.AgreeTerm && !hasReachedEndOfTerm)
              }
              onClick={() => {
                if (currentStep === Step.AgreeTerm) {
                  if (hasReachedEndOfTerm) {
                    gtag.event({
                      action: "click_term_agreement_button",
                      category: "Incheon",
                      label: formatRegistrationNumber(registrationNumber) ?? "",
                      value: canRegister ? 1 : 0,
                    })
                    setHasAgreedTerm(true)
                    setCurrentStep(Step.AllDone)
                  }
                  return
                } else if (canRegister) {
                  gtag.event({
                    action: "click_register_button",
                    category: "Incheon",
                    label: canRegister ? "can_register" : "cannot_register",
                    value: 1,
                  })
                  gtag.event(
                    {
                      action: "인천_회원가입_추천인",
                      category: "Incheon",
                      label: "선택한 추천인",
                    },
                    {
                      select_recommender: recommender,
                    }
                  )
                  register()
                }
              }}
            >
              {currentStep === Step.AgreeTerm
                ? hasReachedEndOfTerm
                  ? "동의합니다"
                  : "약관을 끝까지 읽어주세요"
                : "회원가입 하기"}
            </Button>
          </div>
          {currentStep === Step.ConfirmCompanyInfo && (
            <ConfirmCompanyInfo
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              companyName={companyName}
              companyNameStatus={companyNameStatus}
              setCompanyNameStatus={setCompanyNameStatus}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default SMERegistrationPage
